import { type DBSchema, type IDBPDatabase, openDB } from 'idb'

// Minimal track metadata stored alongside the audio blob so the Downloads page
// and offline player can render without any network access.
export interface DownloadMeta {
  id: number
  title: string
  artist: string
  artistId: number
  album: string
  albumId: number
  duration: number
  coverArt: string | null
}

export interface DownloadRecord {
  trackId: number
  blob: Blob
  mime: string
  bytes: number
  meta: DownloadMeta
  createdAt: number
}

interface OfflineDB extends DBSchema {
  downloads: {
    key: number
    value: DownloadRecord
    indexes: { 'by-album': number }
  }
}

const DB_NAME = 'hypersonic-offline'
const DB_VERSION = 1

let dbPromise: Promise<IDBPDatabase<OfflineDB>> | null = null

export function getOfflineDB(): Promise<IDBPDatabase<OfflineDB>> {
  if (!dbPromise) {
    dbPromise = openDB<OfflineDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const store = db.createObjectStore('downloads', { keyPath: 'trackId' })
        store.createIndex('by-album', 'meta.albumId')
      },
    })
  }
  return dbPromise
}

export async function putDownload(record: DownloadRecord): Promise<void> {
  await (await getOfflineDB()).put('downloads', record)
}

export async function getDownload(trackId: number): Promise<DownloadRecord | undefined> {
  return (await getOfflineDB()).get('downloads', trackId)
}

export async function deleteDownload(trackId: number): Promise<void> {
  await (await getOfflineDB()).delete('downloads', trackId)
}

export async function getDownloadedIds(): Promise<number[]> {
  return (await getOfflineDB()).getAllKeys('downloads')
}

export async function listDownloads(): Promise<DownloadRecord[]> {
  return (await getOfflineDB()).getAll('downloads')
}

export async function clearDownloads(): Promise<void> {
  await (await getOfflineDB()).clear('downloads')
}
